import React from "react";
import ReactHtmlParser from "react-html-parser";

import { useParams } from "react-router-dom";

import Breadcrumb from "components/Breadcrumb";

import Header from "parts/Header";
import Sitemap from "parts/Sitemap";
import Footer from "parts/Footer";
import Slider from "parts/Details/Slider";
import Suggestion from "parts/Details/Suggestion";
import PageErrorMessage from "parts/PageErrorMessage";
import Documents from "parts/Documents";

import { ReactComponent as IconCart } from "assets/images/icon-cart.svg";

import { useGlobalContext } from "helpers/hooks/useGlobalContext";
import useAsync from "helpers/hooks/useAsync";

import fetch from "helpers/fetch";
import "helpers/format/currency";

function LoadingSlider() {
  return (
    <div className="slider">
      <div className="thumbnail">
        {Array(5)
          .fill()
          .map((_, index) => {
            return (
              <div className="px-4 relative card group" key={index}>
                <div
                  className="rounded-xl item bg-gray-300 animate-pulse"
                  style={{ width: 106, height: 106 }}
                ></div>
              </div>
            );
          })}
      </div>
      <div className="preview">
        <div className="item bg-gray-300 animate-pulse rounded-lg h-full overflow-hidden"></div>
      </div>
    </div>
  );
}

function LoadingSuggestion() {
  return (
    <section className="bg-gray-100 px-4 py-16">
      <div className="container mx-auto">
        <div className="flex flex-start mb-4">
          <h3 className="text-2xl capitalize font-semibold">
            <div className="w-40 h-3 bg-gray-400 animate-pulse rounded-full"></div>
            <div className="w-24 h-3 bg-gray-400 animate-pulse mt-3 rounded-full"></div>
          </h3>
        </div>
        <div className="flex overflow-x-auto mb-4 -mx-3">
          {Array(4)
            .fill()
            .map((_, index) => {
              return (
                <div className="px-3 flex-none" key={index}>
                  <div
                    className="rounded-xl overflow-hidden card-shadow relative bg-gray-400 animate-pulse"
                    style={{ width: 287, height: 150 }}
                  ></div>
                  <div className="w-24 h-3 bg-gray-400 animate-pulse mt-3 rounded-full"></div>
                  <div className="w-36 h-3 bg-gray-400 animate-pulse mt-2 rounded-full"></div>
                </div>
              );
            })}
        </div>
      </div>
    </section>
  );
}

export default function DetailsPage() {
  const { idp } = useParams();

  const { data, error, run, isError, isLoading } = useAsync();

  const { dispatch } = useGlobalContext();

  React.useEffect(() => {
    run(fetch({ url: `/api/products/${idp}` }));
  }, [run, idp]);

  return (
    <Documents>
      <Header theme="black" />
      <Breadcrumb
        list={[
          {
            url: "/",
            name: "Home",
          },
          {
            url: "/categories/92134",
            name: "Office Room",
          },
          {
            url: "/categories/92134/products/11351/",
            name: "Details",
          },
        ]}
      />
      {isError ? (
        <PageErrorMessage
          title="Product not found"
          body={error.errors.message}
        />
      ) : (
        <>
          <section className="container mx-auto">
            <div className="flex flex-wrap my-4 md:my-12">
              <div className="w-full md:hidden px-4">
                <h2 className="text-5xl font-semibold">
                  {isLoading ? (
                    <div className="w-80 h-16 bg-gray-300 animate-pulse rounded-full"></div>
                  ) : (
                    data?.title
                  )}
                </h2>
                {isLoading ? (
                  <div className="w-40 h-4 bg-gray-300 animate-pulse rounded-full mt-3"></div>
                ) : (
                  <p className="text-xl">{data?.price?.currency?.()}</p>
                )}
              </div>
              <div className="flex-1">
                {isLoading ? (
                  <LoadingSlider />
                ) : (
                  <Slider data={data?.imgUrls} />
                )}
              </div>
              <div className="flex-1 px-4 md:p-6">
                <h2 className="text-5xl font-semibold">
                  {isLoading ? (
                    <div className="w-80 h-16 bg-gray-300 animate-pulse rounded-full"></div>
                  ) : (
                    data?.title
                  )}
                </h2>

                {isLoading ? (
                  <div className="w-40 h-4 bg-gray-300 animate-pulse rounded-full mt-3"></div>
                ) : (
                  <p className="text-xl">{data?.price?.currency?.()}</p>
                )}

                {isLoading ? (
                  <div className="w-36 h-12 bg-gray-300 animate-pulse rounded-full mt-6"></div>
                ) : (
                  <button
                    className="cart transition-all duration-200 bg-pink-400 text-black focus:bg-black focus:text-pink-400 focus:outline-none rounded-full px-8 py-3 mt-4 inline-flex"
                    onClick={() =>
                      dispatch({
                        type: "ADD_TO_CART",
                        item: data,
                      })
                    }
                  >
                    <IconCart />
                    <span className="ml-3">Add to Cart</span>
                  </button>
                )}
                <hr className="my-8" />

                {isLoading ? (
                  <>
                    <div className="w-36 h-4 bg-gray-300 animate-pulse rounded-full mt-6"></div>
                    <div className="w-88 h-4 bg-gray-300 animate-pulse rounded-full mt-6"></div>
                    <div className="w-40 h-4 bg-gray-300 animate-pulse rounded-full mt-3"></div>
                    <div className="w-96 h-4 bg-gray-300 animate-pulse rounded-full mt-6"></div>
                    <div className="w-64 h-4 bg-gray-300 animate-pulse rounded-full mt-3"></div>
                  </>
                ) : (
                  <>
                    <h6 className="text-xl font-semibold mb-4">
                      About the product
                    </h6>
                    {data.description ? ReactHtmlParser(data.description) : ""}
                  </>
                )}
              </div>
            </div>
          </section>
          {isLoading ? (
            <LoadingSuggestion />
          ) : (
            <Suggestion data={data?.relatedProducts} />
          )}
        </>
      )}

      <Sitemap />
      <Footer />
    </Documents>
  );
}
